import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  Observable,
  CombinedGraphQLErrors
} from '@apollo/client';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import { ErrorLink } from "@apollo/client/link/error";
import { deleteUser } from '../store';
import { store } from "../store";

const SERVER_URL = import.meta.env.VITE_SERVER_URL;

const STORAGE_KEY = 'get-eat-client:auth-headers';

type StoredToken = {
  'access-token'?: string;
  client?: string;
  uid?: string;
  expiry?: string;
  'token-type'?: string;
} | null;

const readStoredToken = (): StoredToken => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredToken) : null;
  } catch {
    return null;
  }
}

const writeStoredToken = (obj: StoredToken) =>{
  try {
    if (obj === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    // noop
  }
}

export const clearStoredToken = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    deleteUser();
  } catch {
    // noop
  }
}

export const isAuthenticated = (): boolean => {
  const stored = readStoredToken();
  if (stored && stored['access-token']) {
    const expiry = parseInt(stored.expiry || '0', 10);
    if (expiry > Date.now() / 1000) {
      return true;
    }
  }
  return false;
}

const authLink = new ApolloLink((operation, forward) => {
  const stored = readStoredToken();
  if (!stored) {
    return forward(operation);
  }

  const context = operation.getContext();
  const headers = context.headers || {};

  operation.setContext({
    headers: {
      ...headers,
      'access-token': stored['access-token'] ?? '',
      client: stored.client ?? '',
      uid: stored.uid ?? '',
      expiry: stored.expiry ?? '',
      'token-type': stored['token-type'] ?? '',
      Authorization:
        stored['token-type'] && stored['access-token']
          ? `${stored['token-type']} ${stored['access-token']}`
          : headers.Authorization ?? '',
    },
  });

  return forward(operation);
});

const handlingErrors = new ErrorLink(({ error }) => {
  if (CombinedGraphQLErrors.is(error)) {
    for (const err of error.errors) {
      switch (err.extensions?.code) {
        case "UNAUTHENTICATED": {  
          const { redirect_to } = err.extensions;
          if (redirect_to) {
            clearStoredToken()
            window.location.href = redirect_to as string;
            return;
          }
          break
        }
        default: {
          console.log(err)
        }
      }
    }
  }
})

const tokenUpdateLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    const observable = forward(operation);
    if (!observable) {
      observer.error(new Error('forward(operation) returned no observable'));
      return;
    }

    const subscription = observable.subscribe({
      next: (response) => {
        if (response.extensions && typeof response.extensions.user_confirmed !== 'undefined') {
          const isConfirmedFromServer = response.extensions.user_confirmed as boolean;
          const currentUser = store.getState().user;
          if (currentUser && currentUser.userConfirmed !== isConfirmedFromServer) {
            store.getState().updateUser({ userConfirmed: isConfirmedFromServer });
          }
        }
        const ctx = operation.getContext() as { response?: Response; fetchResponse?: Response };
        const fetchResponse = ctx.response ?? ctx.fetchResponse;
        const headers = fetchResponse?.headers;
        if (headers && typeof headers.get === 'function') {
          const accessToken = headers.get('access-token');
          const clientHeader = headers.get('client');
          const uid = headers.get('uid');
          const expiry = headers.get('expiry');
          const tokenTypeHeader = headers.get('token-type') || headers.get('authorization');
          const tokenType = tokenTypeHeader ? tokenTypeHeader.split(' ')[0] : undefined;

          if (accessToken || clientHeader || uid || expiry || tokenType) {
            const existing = readStoredToken() || {};
            const updated: StoredToken = {
              ...existing,
              ...(accessToken ? { 'access-token': accessToken } : {}),
              ...(clientHeader ? { client: clientHeader } : {}),
              ...(uid ? { uid } : {}),
              ...(expiry ? { expiry } : {}),
              ...(tokenType ? { 'token-type': tokenType } : {}),
            };
            writeStoredToken(updated);
          }
        }
        observer.next(response);
      },
      error: (err) => observer.error(err),
      complete: () => observer.complete(),
    });

    return () => subscription.unsubscribe();
  });
});

const httpLink = new UploadHttpLink({
  uri: `${SERVER_URL}/graphql`,
  credentials: 'include',
});

const link = authLink.concat(handlingErrors).concat(tokenUpdateLink).concat(httpLink);

const cache = new InMemoryCache();

const client = new ApolloClient({
  link,
  cache,
});

export default client;
export { readStoredToken, writeStoredToken };
